import React, { useState, useEffect, useRef } from 'react';
import { Users, Award, Briefcase, GraduationCap } from 'lucide-react';
import { mockData } from '../mock';

const Team = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="team" ref={sectionRef} className="py-24 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="cyber-grid opacity-5" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-cyan-500/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-r from-blue-500/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 rounded-full glass-effect mb-6">
            <Users className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-400">Nossa Equipe</span>
          </div>
          
          <h2 className="heading-1 mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {mockData.team.title}
            </span>
          </h2>
          
          <p className="body-large text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {mockData.team.description}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          {/* Left Side - Team Leader */}
          <div className={`relative ${isVisible ? 'animate-fadeInLeft' : 'opacity-0'}`}>
            <div className="relative">
              {/* Profile Image */}
              <div className="relative w-80 h-80 mx-auto">
                <img
                  src={mockData.team.leader.image}
                  alt={mockData.team.leader.name}
                  className="w-full h-full object-cover rounded-lg"
                />
                
                {/* Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent rounded-lg" />
                
                {/* Floating Badge */}
                <div className="absolute -top-4 -right-4 glass-effect border border-cyan-400/50 rounded-full p-4 animate-pulse">
                  <Award className="w-8 h-8 text-cyan-400" />
                </div>
                
                {/* Info Card */}
                <div className="absolute bottom-4 left-4 right-4 glass-effect border border-white/20 rounded-lg p-4">
                  <h3 className="heading-3 text-white mb-1">{mockData.team.leader.name}</h3>
                  <p className="text-cyan-400 font-medium text-sm">{mockData.team.leader.position}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Leader Details */}
          <div className={`space-y-8 ${isVisible ? 'animate-fadeInRight' : 'opacity-0'}`}>
            <div>
              <h3 className="heading-2 text-white mb-4">
                Liderança em <span className="text-cyan-400">Cibersegurança</span>
              </h3>
              
              <p className="body-large text-gray-300 leading-relaxed mb-6">
                {mockData.team.leader.description}
              </p>
            </div>

            {/* Expertise Areas */}
            <div className="space-y-4">
              {[
                { icon: Briefcase, title: 'Red Team Operations', description: 'Simulações avançadas de ataques' },
                { icon: GraduationCap, title: 'Certificações', description: 'CISSP, OSCP, CEH e SANS' },
                { icon: Award, title: 'Especialização', description: 'Pentest e Análise de Vulnerabilidades' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                
                return (
                  <div key={index} className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 glass-effect border border-cyan-400/30 rounded-full flex items-center justify-center group-hover:border-cyan-400 group-hover:scale-110 transition-all duration-300">
                      <IconComponent className="w-6 h-6 text-cyan-400" />
                    </div>
                    
                    <div>
                      <h4 className="heading-3 text-white mb-1 group-hover:text-cyan-400 transition-colors duration-300">
                        {item.title}
                      </h4>
                      <p className="body-small text-gray-400">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Team Stats */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-8 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.6s' }}>
          {mockData.team.stats.map((stat, index) => (
            <div
              key={index}
              className="text-center group glass-effect border border-white/10 rounded-lg p-6 hover:border-cyan-400/50 hover:scale-105 transition-all duration-500"
            >
              <div className="text-3xl font-bold text-cyan-400 mb-2 group-hover:scale-110 transition-transform duration-300">
                {stat.value}
              </div>
              <div className="text-gray-400 font-medium group-hover:text-gray-300 transition-colors duration-300">
                {stat.label}
              </div>
              
              {/* Animated Border */}
              <div className="h-1 w-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full mt-4 group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </div>

        {/* Team Values */}
        <div className={`mt-16 glass-effect border border-cyan-400/30 rounded-lg p-8 text-center ${
          isVisible ? 'animate-fadeInUp' : 'opacity-0'
        }`} style={{ animationDelay: '0.8s' }}>
          <h3 className="heading-2 text-white mb-6">
            Compromisso com a <span className="text-cyan-400">Excelência</span>
          </h3>
          
          <p className="body-large text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed">
            Nossa equipe combina anos de experiência prática com conhecimento teórico profundo, 
            garantindo que cada projeto seja executado com o mais alto padrão de qualidade.
          </p>

          {/* Values Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Experiência', description: 'Mais de 10 anos no mercado' },
              { title: 'Certificações', description: 'Profissionais altamente qualificados' },
              { title: 'Atualização', description: 'Sempre atualizados com as últimas ameaças' }
            ].map((value, index) => (
              <div key={index} className="p-4 glass-effect border border-white/10 rounded-lg hover:border-cyan-400/50 transition-all duration-300">
                <h4 className="heading-3 text-cyan-400 mb-2">{value.title}</h4>
                <p className="body-small text-gray-400">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Team;